import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import performanceService from '../../services/data/performance.service';
import moment from "moment";
import 'moment/locale/pt-br'
import { Toast } from 'primereact/toast';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SkeletonProcess from './skeletonProcess';
import ParticipantsSelect from '../../components/performance/participantsSelect';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle'
import { Avatar } from 'primereact/avatar';
import NameInitials from '../../helpers/nameInitials';
import ContentTemplate from '../../components/performance/contentTemplate';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import ContentTemplateSuperior from '../../components/performance/contentTemplateSuperior';
import TypeSteps from '../../components/performance/typeSteps';
import TypeStepsDueDates from '../../components/performance/typeStepsDueDates';
import PeerSelect from '../../components/performance/peerSelect';
import StepWeights from '../../components/performance/stepWeights';
import employeeService from '../../services/data/employee.service';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';


export default function PerformanceTemplateProcess() {

    const VerifyPerms = UsePerms()
    const {rootUser} = useCompanyData()

    const [pageLoad, setPageLoad] = useState(true)
    const toast = useRef(null);
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const _initialSection = location?.state?.section ?? 'PARTICIPANTS'
    const [object, setObject] = useState('')
    const [showDetail, setShowDetail] = useState(_initialSection === 'DETAIL')
    const [showParticipants, setShowParticipants] = useState(_initialSection === 'PARTICIPANTS')
    const [showProcess, setShowProcess] = useState(_initialSection === 'PROCESS')
    const [showSummary, setShowSummary] = useState(_initialSection === 'SUMMARY')
    const [showSuperior, setShowSuperior] = useState(_initialSection === 'SUPERIOR')
    const [showPeers, setShowPeers] = useState(_initialSection === 'PEERS')
    const [showWeights, setShowWeights] = useState(_initialSection === 'WEIGHTS')
    const [, setShowConfirmRemove] = useState(false)
    const [participantToRemove, setParticipantToRemove] = useState(null)
    const [leaderToRemove, setLeaderToRemove] = useState(null)
    const [stepsDueDates, setStepsDueDates] = useState([])
    const [showCloneDialog, setShowCloneDialog] = useState(false)
    const [cloneStartDate, setCloneStartDate] = useState('')
    const [cloneEndDate, setCloneEndDate] = useState('')
    const [cloneResultToMatrix, setCloneResultToMatrix] = useState(false)
    const [cloneTitle, setCloneTitle] = useState('')
    const [cloneSubmitting, setCloneSubmitting] = useState(false)
    const [cloneSuccess, setCloneSuccess] = useState(null)
    const [addingAll, setAddingAll] = useState(false)
    const [partSearch, setPartSearch] = useState('')
    const [partPage, setPartPage] = useState(1)
    const PART_PAGE_SIZE = 10
    const canvaRef = useRef()

    const canShowCloneButton = useMemo(() => {
        if (!object || !object.participants || object.participants.length === 0) return false
        return object.participants.some((p) => p.status !== 'AT')
    }, [object])

    const hideOffCanvas = () => {
        const elem = canvaRef.current
        const modal = bootstrap.Offcanvas.getInstance(elem)
        modal.hide()
    }

    async function handleAddAllCompany() {
        setAddingAll(true)
        try {
            const companyId = localStorage.getItem('companyId')
            let allManagers = []
            let page = 1
            while (true) {
                const res = await employeeService.listManagers(page, { company_id: companyId })
                allManagers = [...allManagers, ...(res.results || [])]
                if (!res.next) break
                page++
            }

            if (allManagers.length === 0) {
                toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Nenhum gestor encontrado.', life: 4000 })
                return
            }

            const participants = allManagers.map((item) => ({
                manager: String(item.id),
                children: (item.children || []).map((ch) => String(ch.id)),
            }))

            const response = await performanceService.addParticipantsPerformanceTemplate({
                company_id: companyId,
                participants,
                id,
            })

            if (object?.assessment_type === 'TD') {
                const peerCalls = []
                for (const group of participants) {
                    if (group.children.length < 2) continue
                    const review = response.participants?.find((r) =>
                        r.managers?.some((m) => String(m.id) === String(group.manager))
                    )
                    if (!review) continue
                    for (let i = 0; i < group.children.length; i++) {
                        for (let j = 0; j < group.children.length; j++) {
                            if (i !== j) {
                                peerCalls.push(performanceService.addPeerStep({
                                    review_id: review.id,
                                    peer_id: group.children[i],
                                    employee_id: group.children[j],
                                }))
                            }
                        }
                    }
                }
                if (peerCalls.length > 0) await Promise.all(peerCalls)
            }

            setObject(response)
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Todos os participantes adicionados com sucesso.', life: 3000 })
        } catch (error) {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: JSON.stringify(error.response?.data || error.message), life: 4000 })
        } finally {
            setAddingAll(false)
        }
    }

    const getObject = useCallback(() => {
        setPageLoad(true)
        performanceService.getPerformanceTemplate(id).then((response) => {
            setObject(response)
            
        }).catch((error) => {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: JSON.stringify(error.response.data), life: 4000 });
        }).finally(() => {
            setPageLoad(false)
        })
    }, [id])

    useEffect(() => {
        getObject()
    }, [getObject])

    useEffect(() => {
        if (modalRef.current) {
            const modal = modalRef.current
            const handleHidden = () => {
                setShowConfirmRemove(false)
                setParticipantToRemove(null)
            }
            
            modal.addEventListener('hidden.bs.modal', handleHidden)
            
            return () => {
                modal.removeEventListener('hidden.bs.modal', handleHidden)
            }
        }
    }, [])

    function openCloneDialog() {
        if (!object) return
        setCloneTitle(`${object.title} (cópia)`)
        setCloneResultToMatrix(!!object.result_to_matrix)
        setCloneStartDate(object.start_date ? moment(object.start_date).format('YYYY-MM-DD') : '')
        setCloneEndDate(object.end_date ? moment(object.end_date).format('YYYY-MM-DD') : '')
        setShowCloneDialog(true)
    }

    function submitClone() {
        if (!cloneStartDate?.trim() || !cloneEndDate?.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe data de início e data de fim.', life: 4000 })
            return
        }
        const startM = moment(cloneStartDate, 'YYYY-MM-DD', true)
        const endM = moment(cloneEndDate, 'YYYY-MM-DD', true)
        if (!startM.isValid() || !endM.isValid()) {
            toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Informe datas válidas.', life: 4000 })
            return
        }
        if (!endM.isAfter(startM, 'day')) {
            toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'A data de fim deve ser maior que a data de início.', life: 4000 })
            return
        }
        setCloneSubmitting(true)
        const data = {
            company_id: localStorage.getItem('companyId'),
            id,
            start_date: startM.format('YYYY-MM-DD'),
            end_date: endM.format('YYYY-MM-DD'),
            result_to_matrix: cloneResultToMatrix,
            title: (cloneTitle || '').trim() || undefined,
        }
        performanceService.clonePerformanceTemplate(data).then((res) => {
            setShowCloneDialog(false)
            setCloneSuccess({ id: res.id, title: res.title })
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Ciclo clonado com sucesso.', life: 5000 })
        }).catch((error) => {
            const detail = error.response?.data
            const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : error.message)
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: msg, life: 5000 })
        }).finally(() => {
            setCloneSubmitting(false)
        })
    }

    function goToClonedTemplate() {
        if (!cloneSuccess?.id) return
        const nextId = cloneSuccess.id
        setCloneSuccess(null)
        navigate(`/performance-template/${nextId}/process/`)
    }
    
    function showSession(val) {

        if (val === 'DETAIL') {
            setShowDetail(true)
        } else {
            setShowDetail(false)
        }

        if (val === 'PARTICIPANTS') {
            setShowParticipants(true)
        } else {
            setShowParticipants(false)
        }

        if (val === 'PROCESS') {
            setShowProcess(true)
        } else {
            setShowProcess(false)
        }

        if (val === 'SUPERIOR') {
            setShowSuperior(true)
        } else {
            setShowSuperior(false)
        }

        if (val === 'SUMMARY') {
            setShowSummary(true)
        } else {
            setShowSummary(false)
        }

        if (val === 'PEERS') {
            setShowPeers(true)
        } else {
            setShowPeers(false)
        }

        if (val === 'WEIGHTS') {
            setShowWeights(true)
        } else {
            setShowWeights(false)
        }
    }

    function importGoals() {
        return navigate(`/performance-template/${id}/import/goals/`, {state: {object: object}})
    }

    function importCompetencies() {
        return navigate(`/performance-template/${id}/import/competencies/`, {state: {object: object}})
    }

    function importQuestions() {
        return navigate(`/performance-template/${id}/import/questions/`, {state: {object: object}})
    }

    function importGoalsSuperior() {
        if (!object.sessionSuperior) {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: 'Sessão "Avaliar Superior" não encontrada neste template.', life: 4000 });
            return;
        }
        return navigate(`/performance-template/${id}/import/goals/`, {state: {object: object, session: object.sessionSuperior.id}})
    }

    function importCompetenciesSuperior() {
        if (!object.sessionSuperior) {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: 'Sessão "Avaliar Superior" não encontrada neste template.', life: 4000 });
            return;
        }
        return navigate(`/performance-template/${id}/import/competencies/`, {state: {object: object, session: object.sessionSuperior.id}})
    }

    function importQuestionsSuperior() {
        if (!object.sessionSuperior) {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: 'Sessão "Avaliar Superior" não encontrada neste template.', life: 4000 });
            return;
        }
        return navigate(`/performance-template/${id}/import/questions/`, {state: {object: object, session: object.sessionSuperior.id}})
    }

    function sendMailStartPerformance() {

        let data = {
            company_id: localStorage.getItem("companyId"),
            id: id,
            steps_due_dates: (Array.isArray(stepsDueDates) && stepsDueDates.length) ? stepsDueDates : null
        }

        performanceService.createReviewsFromPerformanceTemplate(data).then(() => {    
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: `Processo enviado com sucesso`, life: 5000 });
            getObject()
        }).catch((error) => {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: error.response.data, life: 4000 });
        })

    }

    const modalRef = useRef()
    const leaderModalRef = useRef()

    const hideModal = () => {
        const modalEl = modalRef.current
        const bsModal= bootstrap.Modal.getInstance(modalEl)
        bsModal.hide()
    }

    const hideLeaderModal = () => {
        const modalEl = leaderModalRef.current
        const bsModal = bootstrap.Modal.getInstance(modalEl)
        bsModal.hide()
    }

    function confirmRemoveLeader(itemId, managerName) {
        setLeaderToRemove({ itemId, managerName })
    }

    function removeLeader() {
        if (!leaderToRemove) return

        var data = {
            id: leaderToRemove.itemId,
            company_id: localStorage.getItem("companyId")
        }

        performanceService.removeLeaderProcess(data).then(() => {
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Líder e processo removidos com sucesso', life: 3000 })
            getObject()
            setLeaderToRemove(null)
        }).catch((error) => {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: error.response.data, life: 4000 })
        }).finally(() => {
            hideLeaderModal()
        })
    }

    function confirmRemoveParticipant(itemId, empId, empName ) {
        setParticipantToRemove({ itemId, empId, empName })
        setShowConfirmRemove(true)
    }

    function removeParticipant() {
        if (!participantToRemove) return

        var data = {
            id: participantToRemove.itemId,
            employee: participantToRemove.empId,
            company_id: localStorage.getItem("companyId")
        }

        performanceService.removeParticipantPerformanceReview(data).then(() => {
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Participante removido com sucesso', life: 3000 });
            getObject()
            setShowConfirmRemove(false)
            setParticipantToRemove(null)
        }).catch((error) => {
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: error.response.data, life: 4000 });
        }).finally(() => {
            hideModal()
        })
    }

    return (
        <ProtectedRoute>
            
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <div className='col text-bg-green p-3'>
                        <a href='/painel/' className="fs-6 fw-bold ps-5 text-decoration-none text-white">
                            <FontAwesomeIcon className='' icon={solid('arrow-left')} /> { object && object.status === 'EC' ? <>Editar Ciclo</> : <>Ver Ciclo</> }
                        </a>
                    </div>
                </div>
                <div className='my-1 mx-5 py-3 rounded-top' style={{minHeight: '500px'}}>

                    {
                        pageLoad ?
                        <SkeletonProcess />
                        :
                        <>
                        <div className='bg-white p-3 mb-2 rounded border-clean'>
                            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                                <span className='fw-bold fs-6'>{object.title}</span>
                                {
                                    canShowCloneButton ?
                                    <button type='button' className='btn btn-sm btn-outline-success btn-rounded px-3' onClick={openCloneDialog}>
                                        <FontAwesomeIcon className='me-1' icon={solid('copy')} /> Clonar ciclo
                                    </button>
                                    : null
                                }
                            </div>
                        </div>
                        {
                            cloneSuccess ?
                            <div className='alert alert-success border-clean d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2'>
                                <div>
                                    <span className='fw-bold'>Clonagem concluída.</span>
                                    <span className='ms-1'>O ciclo <strong>{cloneSuccess.title}</strong> foi criado.</span>
                                </div>
                                <button type='button' className='btn btn-sm btn-success btn-rounded px-3' onClick={goToClonedTemplate}>
                                    Abrir novo ciclo
                                </button>
                            </div>
                            : null
                        }
                        <div className='d-flex bg-white rounded border-clean'>
                            
                            <div className='flex-column py-2 col-2 border-end'>
                                <nav role={ !showDetail ? 'button' : null} className={ showDetail ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('DETAIL')}>
                                    <li className='nav-item py-2 border-bottom'>
                                        <span className={ showDetail ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Detalhes</span>
                                    </li>
                                </nav>
                                <nav role={ !showParticipants ? 'button' : null} className={ showParticipants ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('PARTICIPANTS')}>
                                    <li className='nav-item py-2 border-bottom'>
                                        <span className={ showParticipants ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Participantes</span>
                                    </li>
                                </nav>
                                {
                                    object.assessment_type === 'TD' ?
                                    <>
                                    <nav role={ !showPeers ? 'button' : null} className={ showPeers ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('PEERS')}>
                                        <li className='nav-item py-2 border-bottom'>
                                            <span className={ showPeers ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Pares</span>
                                        </li>
                                    </nav>
                                    </>
                                    : null
                                }
                                <nav role={ !showProcess ? 'button' : null} className={ showProcess ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('PROCESS')}>
                                    <li className='nav-item py-2 border-bottom'>
                                        <span className={ showProcess ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Conteúdo</span>
                                    </li>
                                </nav>
                                {
                                    object.assessment_type === 'SD' || object.assessment_type === 'TD' ?
                                    <nav role={ !showSuperior ? 'button' : null} className={ showSuperior ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('SUPERIOR')}>
                                        <li className='nav-item py-2 border-bottom'>
                                            <span className={ showSuperior ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Avaliar Superior</span>
                                        </li>
                                    </nav>
                                    : null
                                }
                                <nav role={ !showWeights ? 'button' : null} className={ showWeights ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('WEIGHTS')}>
                                    <li className='nav-item py-2 border-bottom'>
                                        <span className={ showWeights ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Pesos das Etapas</span>
                                    </li>
                                </nav>
                                <nav role={ !showSummary ? 'button' : null} className={ showSummary ? 'nav flex-column' : 'nav flex-column bg-default navColHover'} onClick={() => showSession('SUMMARY')}>
                                    <li className='nav-item py-2 border-bottom'>
                                        <span className={ showSummary ? 'nav-link text-default fw-bold' : 'nav-link text-grey'}>Revisar e Confirmar</span>
                                    </li>
                                </nav>
                            </div>

                            <div className='flex-column col-10 paddingBottom200'>
                                <div className='p-4 mb-5'>
                                    {
                                        showDetail ?
                                        <>
                                        <p className='fs-6 fw-bold ps-3'>Detalhes</p>
                                        <hr/>
                                        <div className='ps-3'>
                                            <div>
                                                <strong>Título</strong>
                                                <p>{object.title}</p>
                                            </div>
                                            <div>
                                                <strong>Template</strong>
                                                <p>{object.assessmentTypeDisplay}</p>
                                            </div>
                                            <div>
                                                <strong>Criado em</strong>
                                                <p>{moment(object.created).format("DD-MMM-YYYY")}</p>
                                            </div>
                                            <div>
                                                <strong>Período</strong>
                                                <p>{moment(object.start_date).format("DD-MMM-YYYY")} - {moment(object.end_date).format("DD-MMM-YYYY")} </p>
                                            </div>
                                            <div>
                                                <strong>Status</strong>
                                                <p>{object.statusDisplay}</p>
                                            </div>
                                            <div>
                                                <strong>Considerar Resultado na Matriz 9Box?</strong>
                                                <p>
                                                    {
                                                        object.result_to_matrix ?
                                                        'Sim'
                                                        :
                                                        'Não'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                        </>
                                        : null
                                    }
                                    {
                                        showParticipants ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Participantes</div>
                                            <div>
                                                {
                                                    object && object.status === 'EC' ?
                                                    <div className="btn-group">
                                                        <button type="button" className="btn btn-sm btn-success btn-rounded px-3 dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                                                            Adicionar
                                                        </button>
                                                        <ul className="dropdown-menu dropdown-menu-end border headerBorder font1em">
                                                            <li>
                                                                <button className="dropdown-item" data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddPart">
                                                                    Selecionar participantes
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item" onClick={handleAddAllCompany} disabled={addingAll}>
                                                                    {addingAll
                                                                        ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Adicionando...</>
                                                                        : 'Enviar para toda empresa'
                                                                    }
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    :
                                                    <button className='btn btn-sm btn-success btn-rounded px-3 disabled'>Adicionar</button>
                                                }
                                                
                                            </div>
                                        </div>
                                        
                                        <hr/>
                                        {
                                            object.participants && object.participants.length > 0 ?
                                            (() => {
                                                const searchLower = partSearch.toLowerCase()
                                                const filtered = object.participants.filter((item) => {
                                                    if (!searchLower) return true
                                                    const inManagers = item.managers?.some((m) => m.user.full_name.toLowerCase().includes(searchLower))
                                                    const inEmployees = item.employees?.some((e) => e.user.full_name.toLowerCase().includes(searchLower))
                                                    return inManagers || inEmployees
                                                })
                                                const totalPages = Math.ceil(filtered.length / PART_PAGE_SIZE)
                                                const page = Math.min(partPage, totalPages || 1)
                                                const paginated = filtered.slice((page - 1) * PART_PAGE_SIZE, page * PART_PAGE_SIZE)

                                                return (
                                                <div className='px-3'>
                                                    <div className='d-flex align-items-center justify-content-between mb-2'>
                                                        <p className='ps-1 mb-0'>
                                                            <span className='fw-bold'>{object.participants.length}</span> Avaliadores Participando
                                                        </p>
                                                        <div className='input-group input-group-sm' style={{maxWidth: '260px'}}>
                                                            <span className='input-group-text bg-transparent'>
                                                                <FontAwesomeIcon icon={solid('magnifying-glass')} className='text-muted' />
                                                            </span>
                                                            <input
                                                                type='text'
                                                                className='form-control'
                                                                placeholder='Buscar participante...'
                                                                value={partSearch}
                                                                onChange={(e) => { setPartSearch(e.target.value); setPartPage(1) }}
                                                            />
                                                            {partSearch && (
                                                                <button type='button' className='btn btn-outline-secondary' onClick={() => { setPartSearch(''); setPartPage(1) }}>
                                                                    <FontAwesomeIcon icon={solid('times')} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <table className='table table-bordered text-default borderBottom mt-2'>
                                                        <tbody>
                                                        {
                                                            paginated.map((item, index) =>
                                                            <React.Fragment key={item.id || index}>
                                                            {
                                                                item.managers.length > 0 ?
                                                                <>
                                                                {
                                                                    item.managers.map((m, _i) =>
                                                                    <tr key={m.id} className='bg-light'>
                                                                        <td className="align-middle border-end-0" width='20px'>
                                                                            <div className='d-flex justify-content-between align-items-center w-100'>
                                                                                <div>
                                                                                {
                                                                                    m.user.photo ?
                                                                                    <Avatar title={m.user.full_name} image={m.user.photo} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                                                    :
                                                                                    <Avatar title={m.user.full_name} label={NameInitials( m.user.full_name )} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle'/>
                                                                                }
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="align-middle border-start-0">
                                                                            <span className='fw-bold text-decoration-none'>
                                                                                {m.user.full_name}
                                                                            </span>
                                                                            <a href={`/performance-review/${item.id}/process/`} target='_blank' className='ms-1 btn btn-xs rounded-circle' rel="noreferrer">
                                                                                <FontAwesomeIcon className='text-green font095' icon={solid('share-from-square')} />
                                                                            </a>
                                                                        </td>
                                                                        <td className="align-middle text-end">
                                                                            {object.status === 'EC' &&
                                                                                <button className='btn btn-xs font095 text-danger' onClick={() => confirmRemoveLeader(item.id, m.user.full_name)} data-bs-toggle="modal" data-bs-target="#confirmRemoveLeaderModal">
                                                                                    Remover
                                                                                </button>
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                    )
                                                                }
                                                                </>
                                                                : null
                                                            }
                                                            {
                                                                item.employees.length > 0 ?
                                                                <tr className='border-top border-end'>
                                                                    <td colSpan='3'>
                                                                        <table className='table table-borderless'>
                                                                            <tbody>
                                                                            {
                                                                                item.employees.map((emp) =>
                                                                                <tr key={emp.id}>
                                                                                    <td className="align-middle" width='20px'>
                                                                                        {
                                                                                            emp.user.photo ?
                                                                                            <Avatar title={emp.user.full_name} image={emp.user.photo} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                                                            :
                                                                                            <Avatar title={emp.user.full_name} label={NameInitials( emp.user.full_name )} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle'/>
                                                                                        }
                                                                                    </td>
                                                                                    <td className="align-middle">
                                                                                        <span className='fw-bold text-decoration-none'>
                                                                                            {emp.user.full_name}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        <button className='btn btn-xs font095 text-danger' onClick={() => confirmRemoveParticipant(item.id, emp.id, emp.user.full_name)} data-bs-toggle="modal" data-bs-target="#confirmRemoveModal">
                                                                                            Remover
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                                )
                                                                            }
                                                                            </tbody>
                                                                        </table>
                                                                    </td>
                                                                </tr>
                                                                : null
                                                            }
                                                            </React.Fragment>
                                                            )
                                                        }
                                                        </tbody>
                                                    </table>
                                                    {totalPages > 1 && (
                                                        <div className='d-flex align-items-center justify-content-between mt-2'>
                                                            <small className='text-muted'>
                                                                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''} · página {page} de {totalPages}
                                                            </small>
                                                            <nav>
                                                                <ul className='pagination pagination-sm mb-0'>
                                                                    <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                                                                        <span className='page-link' role='button' onClick={() => setPartPage(page - 1)}>
                                                                            <FontAwesomeIcon icon={solid('angle-left')} />
                                                                        </span>
                                                                    </li>
                                                                    <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                                                                        <span className='page-link' role='button' onClick={() => setPartPage(page + 1)}>
                                                                            <FontAwesomeIcon icon={solid('angle-right')} />
                                                                        </span>
                                                                    </li>
                                                                </ul>
                                                            </nav>
                                                        </div>
                                                    )}
                                                </div>
                                                )
                                            })()
                                            :
                                            <>
                                            <div className='alert alert-warning'>
                                                <label className='fw-bold pe-3'>Nenhum participante adicionado ainda</label>
                                            </div>
                                            </>
                                        }

                                        <div ref={canvaRef} className='offcanvas offcanvas-end' style={{'--bs-offcanvas-width': 'min(95vw, 90vw)'}} data-bs-backdrop="static" id='offCanvasAddPart' tabIndex={-1}>
                                            <div className='offcanvas-header border-bottom'>
                                                <h6 className='offcanvas-title'>Adicionar Participantes</h6>
                                                <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                                            </div>
                                            <div className='offcanvas-body'>
                                                <div className=''>
                                                    <ParticipantsSelect setObject={setObject} id={id} hideModal={hideOffCanvas} assessmentType={object?.assessment_type} />
                                                </div>
                                            </div>
                                        </div>
                                        </>
                                        : null
                                    }
                                    {
                                        showProcess ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Conteúdo</div>
                                            <div>
                                                {
                                                    object && object.status === 'EC' ?
                                                    <>
                                                    <div className='dropdown' role="group" style={{'fontSize': '12px'}}>
                                                        <button className='btn btn-sm btn-success btn-rounded px-3' data-bs-toggle="dropdown" aria-expanded="false">
                                                            Adicionar
                                                        </button>
                                                        <ul className='dropdown-menu border-clean headerBorder font1em mt-1' aria-labelledby="dropdownOptn">
                                                            <li>
                                                                <span className='dropdown-header'>Objetivos</span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddGoal">
                                                                    Adicionar Novo Objetivo
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' onClick={() => {importGoals()}}>
                                                                    Importar Objetivos
                                                                </span>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <span className='dropdown-header'>Competências</span>
                                                            </li>
                                                            {
                                                                VerifyPerms(['add_competencies']) || rootUser ?
                                                                <>
                                                                <li>
                                                                    <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddComp" >
                                                                        Adicionar Nova Competência
                                                                    </span>
                                                                </li>
                                                                </>
                                                                : null
                                                            }
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default'  onClick={() => {importCompetencies()}}>
                                                                    Importar Competência
                                                                </span>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <span className='dropdown-header'>Perguntas</span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddQuestion">
                                                                    Adicionar Nova Pergunta
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' onClick={() => {importQuestions()}}>
                                                                    Importar Perguntas
                                                                </span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    </>
                                                    :
                                                    <button className='btn btn-sm btn-success btn-rounded px-3 disabled'>
                                                        Adicionar
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                        <hr/>
                                        <ContentTemplate object={object} updateObject={getObject} />
                                        </>
                                        : null
                                    }
                                    {
                                        showSuperior ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Avaliar Superior</div>
                                            <div>
                                                {
                                                    object && object.status === 'EC' ?
                                                    <>
                                                    <div className='dropdown' role="group" style={{'fontSize': '12px'}}>
                                                        <button className='btn btn-sm btn-success btn-rounded px-3' data-bs-toggle="dropdown" aria-expanded="false">
                                                            Adicionar
                                                        </button>
                                                        <ul className='dropdown-menu border-clean headerBorder font1em mt-1' aria-labelledby="dropdownOptn">
                                                            <li>
                                                                <span className='dropdown-header'>Objetivos</span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddGoal">
                                                                    Adicionar Novo Objetivo
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' onClick={() => {importGoalsSuperior()}}>
                                                                    Importar Objetivos
                                                                </span>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <span className='dropdown-header'>Competências</span>
                                                            </li>
                                                            {
                                                                VerifyPerms(['add_competencies']) || rootUser ?
                                                                <>
                                                                <li>
                                                                    <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddComp" >
                                                                        Adicionar Nova Competência
                                                                    </span>
                                                                </li>
                                                                </>
                                                                : null
                                                            }
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default'  onClick={() => {importCompetenciesSuperior()}}>
                                                                    Importar Competência
                                                                </span>
                                                            </li>
                                                            <li><hr className="dropdown-divider" /></li>
                                                            <li>
                                                                <span className='dropdown-header'>Perguntas</span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' data-bs-toggle="offcanvas" data-bs-target="#offCanvasAddQuestion">
                                                                    Adicionar Nova Pergunta
                                                                </span>
                                                            </li>
                                                            <li>
                                                                <span role='button' className='dropdown-item text-default' onClick={() => {importQuestionsSuperior()}}>
                                                                    Importar Perguntas
                                                                </span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    </>
                                                    :
                                                    <button className='btn btn-sm btn-success btn-rounded px-3 disabled'>
                                                        Adicionar
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                        <hr/>
                                        <ContentTemplateSuperior object={object} session={object.sessionSuperior} updateObject={getObject} />
                                        </>
                                        : null
                                    }
                                    {
                                        showPeers ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Pares</div>
                                        </div>
                                        <hr/>
                                        <div className='px-3'>
                                            <p className='text-muted small mb-3'>
                                                Defina quais colaboradores irão avaliar cada avaliado neste ciclo. Os pares só podem ser adicionados após incluir os participantes.
                                            </p>
                                            {
                                                object.participants && object.participants.length > 0 ?
                                                <PeerSelect
                                                    participants={object.participants}
                                                    peerData={object.peers}
                                                    onUpdate={getObject}
                                                />
                                                :
                                                <p className='text-muted'>Adicione participantes primeiro para definir os pares.</p>
                                            }
                                        </div>
                                        </>
                                        : null
                                    }
                                    {
                                        showWeights ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Pesos das Etapas</div>
                                        </div>
                                        <hr/>
                                        <div className='px-3' style={{maxWidth: '500px'}}>
                                            <p className='text-muted small mb-3'>
                                                Configure o peso de cada etapa no cálculo da nota final. A soma deve ser exatamente 100%.
                                            </p>
                                            <StepWeights
                                                templateId={object.id}
                                                assessmentType={object.assessment_type}
                                                readOnly={object.status !== 'EC'}
                                            />
                                        </div>
                                        </>
                                        : null
                                    }
                                    {
                                        showSummary ?
                                        <>
                                        <div className='d-flex justify-content-between align-items-center'>
                                            <div className='fs-6 fw-bold ps-3'>Revisar e Confirmar</div>
                                            <div>
                                                {
                                                    object && object.status === 'EC' ?
                                                    <button className='float-end btn btn-sm btn-success btn-rounded px-3' data-bs-toggle="offcanvas" data-bs-target="#offCanvasSend">Enviar</button>
                                                    :
                                                    <button className='float-end btn btn-sm btn-success btn-rounded px-3 disabled'>Enviar</button>
                                                }
                                            </div>
                                        </div>
                                        <hr/>

                                        <div className='row'>
                                            <div className='col'>
                                                
                                                <div className='border p-2 rounded m-1'>
                                                    <table className='table font1em mb-0'>
                                                        <thead>
                                                            <tr>
                                                                <th style={{fontSize: '14px'}}>Detalhes</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className='pb-0'>
                                                                <td className='pb-0'>
                                                                    <div>
                                                                        <strong>Período</strong>
                                                                        <p>{moment(object.start_date).format("DD-MMM-YYYY")} - {moment(object.end_date).format("DD-MMM-YYYY")} </p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Status</strong>
                                                                        <p>{object.statusDisplay}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Considerar Resultado na Matriz 9Box?</strong>
                                                                        <p>
                                                                            {
                                                                                object.result_to_matrix ?
                                                                                'Sim'
                                                                                :
                                                                                'Não'
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                               
                                            </div>
                                        </div>

                                        <div className='row'>
                                            <div className='col'>
                                                
                                                <div className='border p-2 rounded m-1'>
                                                    <table className='table font1em mb-0'>
                                                        <thead>
                                                            <tr>
                                                                <th style={{fontSize: '14px'}}>{ object.participants ? <>({object.participants.length})</> : null} Participantes</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className='pb-0'>
                                                                <td className='pb-0'>
                                                                    {
                                                                        object.participants && object.participants.length > 0 ?
                                                                        <>
                                                                        {
                                                                            object.participants.map((item, index) =>
                                                                            <React.Fragment key={index}>
                                                                            {
                                                                                item.managers.length > 0 ?
                                                                                <table>
                                                                                    <tbody>
                                                                                    {
                                                                                        item.managers.map((m, i) => 
                                                                                        <tr key={i} className=''>
                                                                                            <td className="align-middle border-end-0 py-1" width='20px'>
                                                                                                <span>
                                                                                                {
                                                                                                    m.user.photo ?
                                                                                                    <Avatar title={m.user.full_name} image={m.user.photo} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle' />
                                                                                                    :
                                                                                                    <Avatar title={m.user.full_name} label={NameInitials( m.user.full_name )} style={{ backgroundColor: '#42c0fb', color: '#ffffff', fontWeight: 'bold' }} shape='circle'/>
                                                                                                }
                                                                                                </span>
                                                                                            </td>
                                                                                            <td className="align-middle ps-1 border-start-0">
                                                                                                <span className='fw-bold text-decoration-none'>
                                                                                                    {m.user.full_name}
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                        )
                                                                                    }
                                                                                    </tbody>
                                                                                </table>
                                                                                : null
                                                                            }
                                                                            </React.Fragment>
                                                                            )
                                                                        }
                                                                        </>
                                                                        :
                                                                        <div className='alert alert-warning'>
                                                                            <label className='fw-bold pe-3'>Nenhum participante adicionado ainda</label>
                                                                        </div>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                               
                                            </div>
                                        </div>

                                        <div className='row'>
                                            <div className='col'>
                                                <div className='border p-2 rounded m-1'>
                                                    <table className='table font1em mb-0'>
                                                        <thead>
                                                            <tr>
                                                                <th style={{fontSize: '14px'}}>Etapas</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                        {
                                                            object ?
                                                            <tr>
                                                            <td className='ps-1'>
                                                            <TypeSteps type={object.assessment_type} />
                                                            </td>
                                                            </tr>
                                                            : null
                                                        }
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                        <div className='row'>
                                            <div className='col'>
                                                
                                                <div className='border p-2 rounded m-1'>
                                                    <table className='table table-borderless font1em mb-0'>
                                                        <thead className='border-bottom'>
                                                            <tr>
                                                                <th style={{fontSize: '14px'}}>Conteúdo</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className='pt-2'>
                                                            {
                                                                object.goals.length > 0 ?
                                                                <>
                                                                <tr className='align-middle '>
                                                                    <td className=''><span className='fw-bold'>{object.goals.length}</span> - Objetivos</td>
                                                                </tr>
                                                                </>
                                                                : null
                                                            }
                                                            {
                                                                object.competencies.length > 0 ?
                                                                <>
                                                                <tr className='align-middle '>
                                                                    <td className=''><span className='fw-bold'>{object.competencies.length}</span> - Competências</td>
                                                                </tr>
                                                                </>
                                                                : null
                                                            }
                                                            {
                                                                object.questions.length > 0 ?
                                                                <>
                                                                <tr className='align-middle '>
                                                                    <td className=''><span className='fw-bold'>{object.questions.length}</span> - Perguntas</td>
                                                                </tr>
                                                                </>
                                                                : null
                                                            }
                                                        </tbody>
                                                    </table>
                                                </div>
                                               
                                            </div>
                                        </div>

                                        {
                                            object.sessionSuperior ?
                                            <>
                                            <div className='row'>
                                                <div className='col'>
                                                    
                                                    <div className='border p-2 rounded m-1'>
                                                        <table className='table table-borderless font1em mb-0'>
                                                            <thead className='border-bottom'>
                                                                <tr>
                                                                    <th style={{fontSize: '14px'}}>Conteúdo para Avaliar Superior</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className='pt-2'>
                                                                {
                                                                    object.sessionSuperior.goals.length > 0 ?
                                                                    <>
                                                                    <tr className='align-middle '>
                                                                        <td className=''><span className='fw-bold'>{object.sessionSuperior.goals.length}</span> - Objetivos</td>
                                                                    </tr>
                                                                    </>
                                                                    : null
                                                                }
                                                                {
                                                                    object.sessionSuperior.competencies.length > 0 ?
                                                                    <>
                                                                    <tr className='align-middle '>
                                                                        <td className=''><span className='fw-bold'>{object.sessionSuperior.competencies.length}</span> - Competências</td>
                                                                    </tr>
                                                                    </>
                                                                    : null
                                                                }
                                                                {
                                                                    object.sessionSuperior.questions.length > 0 ?
                                                                    <>
                                                                    <tr className='align-middle '>
                                                                        <td className=''><span className='fw-bold'>{object.sessionSuperior.questions.length}</span> - Perguntas</td>
                                                                    </tr>
                                                                    </>
                                                                    : null
                                                                }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                
                                                </div>
                                            </div>
                                            </>
                                            : null
                                        }

                                        <div className='row my-2'>
                                            <div className='col'>
                                                {
                                                    object && object.status === 'EC' ?
                                                    <button className='float-end btn btn-sm btn-success btn-rounded px-3' data-bs-toggle="offcanvas" data-bs-target="#offCanvasSend">Enviar</button>
                                                    : null
                                                }
                                            </div>
                                        </div>

                                        <div className='offcanvas offcanvas-end offcanvas-size-xl' data-bs-backdrop="static" id='offCanvasSend' tabIndex={-1}>
                                            <div className='offcanvas-header'>
                                                <h6 className='offcanvas-title'>Enviar</h6>
                                                <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                                            </div>
                                            <div className='offcanvas-body'>
                                                <div className='p-3 bg-light rounded'>
                                                    <p>Tem certeza de que deseja enviar para <span className='fw-bold'> { object.participants ? <>{object.participants.length}</> : null}</span> pessoas a avaliação <span className='fw-bold'>{object.title}</span> ?</p>
                                                      
                                                    <TypeStepsDueDates type={object.assessment_type} onChangeSteps={setStepsDueDates} startDate={object.start_date} endDate={object.end_date} />
                                                    <button className='btn btn-sm btn-success btn-rounded px-3 mt-3' onClick={() => sendMailStartPerformance()}>
                                                        Sim, enviar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        </>
                                        : null
                                    }
                                </div>
                            </div>
                        </div>
                        </>
                    }
                </div>
            </div>

            <Dialog
                header="Clonar ciclo de performance"
                visible={showCloneDialog}
                style={{ width: 'min(96vw, 520px)' }}
                onHide={() => !cloneSubmitting && setShowCloneDialog(false)}
                footer={
                    <div className='d-flex justify-content-end gap-2'>
                        <button type='button' className='btn btn-sm btn-light btn-rounded px-3' disabled={cloneSubmitting} onClick={() => setShowCloneDialog(false)}>Cancelar</button>
                        <button type='button' className='btn btn-sm btn-success btn-rounded px-3' disabled={cloneSubmitting} onClick={submitClone}>Confirmar clonagem</button>
                    </div>
                }
            >
                <p className='small text-muted mb-3'>Defina o período e se o resultado entra na matriz 9 box. As novas avaliações serão criadas como <strong>não iniciadas</strong> (status AT).</p>
                <div className='mb-3'>
                    <label className='form-label small fw-bold'>Título do novo ciclo</label>
                    <input type='text' className='form-control form-control-sm' value={cloneTitle} onChange={(e) => setCloneTitle(e.target.value)} disabled={cloneSubmitting} />
                </div>
                <div className='row g-3'>
                    <div className='col'>
                        <label className="form-label fw-bold">Data de Início <span className="text-danger">*</span></label>
                        <input
                            className="form-control"
                            name="clone_start_date"
                            type="date"
                            required
                            autoComplete="off"
                            value={cloneStartDate}
                            onChange={(e) => setCloneStartDate(e.target.value)}
                            disabled={cloneSubmitting}
                        />
                    </div>
                    <div className='col'>
                        <label className="form-label fw-bold">Data de Término <span className="text-danger">*</span></label>
                        <input
                            className="form-control"
                            name="clone_end_date"
                            type="date"
                            required
                            autoComplete="off"
                            value={cloneEndDate}
                            onChange={(e) => setCloneEndDate(e.target.value)}
                            disabled={cloneSubmitting}
                        />
                    </div>
                </div>
                <div className="form-check form-switch mb-4 mt-3">
                    <label className="form-switch-label">Considerar Resultado na Matriz 9Box?</label>
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        name="result_to_matrix"
                        aria-checked={cloneResultToMatrix}
                        checked={cloneResultToMatrix}
                        onChange={(e) => setCloneResultToMatrix(e.target.checked)}
                        disabled={cloneSubmitting}
                    />
                </div>
                {cloneSubmitting ? (
                    <div className='mt-3'>
                        <p className='small text-muted mb-2'>Clonando conteúdo e participantes…</p>
                        <ProgressBar mode='indeterminate' style={{ height: '8px' }} />
                    </div>
                ) : null}
            </Dialog>

            {/* Confirmation Modal */}
            <div className="modal fade" id="confirmRemoveModal" ref={modalRef} tabIndex={-1} aria-labelledby="confirmRemoveModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="confirmRemoveModalLabel">Confirmar Remoção</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Tem certeza de que deseja remover este participante?</p>
                            <p>{participantToRemove?.empName}</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-sm btn-danger" onClick={removeParticipant}>
                                Remover
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal - Remove Leader */}
            <div className="modal fade" id="confirmRemoveLeaderModal" ref={leaderModalRef} tabIndex={-1} aria-labelledby="confirmRemoveLeaderModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="confirmRemoveLeaderModalLabel">Remover Líder</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Tem certeza de que deseja remover este líder e todo o seu processo?</p>
                            <p className='fw-bold'>{leaderToRemove?.managerName}</p>
                            <p className='text-danger small mb-0'>Esta ação irá remover o líder e todos os seus liderados deste ciclo.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-sm btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={removeLeader}>
                                Remover
                            </button>
                        </div>
                    </div>
                </div>
            </div>


        </ProtectedRoute>
    )
}